import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Table, TableRow, TableCell, TableBody, Checkbox, FormHelperText } from '@mui/material';
import { Dialog, Button, Autocomplete } from '@totalsoft/rocket-ui';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { saveOfferPartnerAddresses, loadPartnerAdresses } from '../actionCreators';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { concatFailure, Validator, Failure, ValidationError, Success } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { defaultAddressTypes } from 'constants/defaultAddressTypes';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { BoldTableHead, BoxActionsWrapper } from 'components/common/CommonStyles';

const AddressesModal = ({ addressTypes, documentId, siteId, partner, partnerAddresses, t, socialAddressTypeId, closeAddressesModal }) => {

  const mandatorySocialAddressValidator = Validator(function mandatorySocialAddressValidator(content) {
    return partnerAddresses != null && isAtLeastOneSocialAddress(content)
      ? Success
      : Failure(ValidationError(t('OfferEdit.Client.PartnerAddresses.Validation')));
  });
 
  const [saveInProgress, setSaveInProgress] = useState(false);

  const validatorAddresses = [mandatorySocialAddressValidator] |> concatFailure;

  const partnerAddressesLoaded = useSelector(
    state =>
      state.currentOffer.clientAddressesData[partner?.partnerId]
        ? state.currentOffer.clientAddressesData[partner?.partnerId].loaded
        : false,
    shallowEqual
  );

  useEffect(() => {
    if (partnerAddressesLoaded) setSaveInProgress(false);
  }, [partnerAddressesLoaded, setSaveInProgress])

  const [localPartnerAddresses, setLocalPartnerAddresses] = useState([]);
  const dispatch = useDispatch();
  const [validationAddresses, validateAddresses] = useDirtyFieldValidation(validatorAddresses);

  useEffect(() => {
    if (partnerAddressesLoaded) setLocalPartnerAddresses(partnerAddresses);
    else dispatch(loadPartnerAdresses(documentId, siteId, partner?.partnerId));
  }, [partnerAddressesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPropertyChange = (idx, propertyName) => event => {
    setLocalPartnerAddresses(
      Object.assign([], {
        ...localPartnerAddresses,
        [idx]: {
          ...localPartnerAddresses[idx],
          [propertyName]: propertyName === 'isActive' ? event.target.checked : event,
          isDefault: defaultAddressTypes.find(item => item === localPartnerAddresses[idx].addressTypeCode) ? true : false
        }
      })
    );
  };

  const handleSave = () => {
    if (!validateAddresses(localPartnerAddresses)) return;

    setSaveInProgress(true);
    dispatch(saveOfferPartnerAddresses(documentId, siteId, partner.partnerId, localPartnerAddresses));
  };

  const isAtLeastOneSocialAddress = partnerAddresses => {
    return (
      partnerAddresses.filter(partnerAddress => partnerAddress.addressTypeId === socialAddressTypeId && partnerAddress.isActive).length ===
      1
    );
  };

  return (
    <Dialog
      open={true}
      aria-labelledby='addresses-dialog-title'
      maxWidth='xl'
      fullWidth
      disableEscapeKeyDown
      title={t('OfferEdit.Client.PartnerAddresses.Title')}
      onClose={closeAddressesModal}
      content={
        <>
          <Grid container>
            {(saveInProgress ? <WrappedFakeText /> :
              <Grid item xs={12} md={12} sm={12}>
                <Table>
                  <BoldTableHead>
                    <TableRow>
                      <TableCell>{t('OfferEdit.Client.PartnerAddresses.Active')}</TableCell>
                      <TableCell>{t('OfferEdit.Client.PartnerAddresses.AddressType')}</TableCell>
                      <TableCell>{t('OfferEdit.Client.PartnerAddresses.Address')}</TableCell>
                    </TableRow>
                  </BoldTableHead>
                  <TableBody>
                    {
                      localPartnerAddresses.map((partnerAddress, index) => {
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Checkbox checked={partnerAddress.isActive} onChange={onPropertyChange(index, 'isActive')} />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                fullWidth
                                value={partnerAddress.addressTypeId}
                                isSearchable={false}
                                simpleValue={true}
                                onChange={onPropertyChange(index, 'addressTypeId')}
                                valueKey='id'
                                labelKey='name'
                                options={addressTypes}
                                isClearable={false}
                              />
                            </TableCell>
                            <TableCell>{partnerAddress.syntheticName}</TableCell>
                          </TableRow>
                        );
                      })
                    }
                  </TableBody>
                </Table>
              </Grid>
            )}
          </Grid>{' '}
          {!isValid(validationAddresses) && (
            <FormHelperText error={true} id='name-error-text'>
              {getErrors(validationAddresses)}
            </FormHelperText>
          )}
        </>
      }
      actions={
        <BoxActionsWrapper forDialog>
          <Button id={'offerPartnerFormCard_addressesModal_saveBtn'} onClick={handleSave} disabled={saveInProgress}>
            {t('Save')}
          </Button>
          <Button id={'offerPartnerFormCard_addressesModal_closeBtn'} color='primary' variant='outlined' onClick={closeAddressesModal}>
            {t('Close')}
          </Button>
        </BoxActionsWrapper>
      }
    />
  );
};

AddressesModal.propTypes = {
  socialAddressTypeId: PropTypes.number.isRequired,
  addressTypes: PropTypes.array.isRequired,
  partnerAddresses: PropTypes.array.isRequired,
  closeAddressesModal: PropTypes.func,
  partner: PropTypes.object,
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default compose(withTranslation())(AddressesModal);
